import { z } from 'zod';
import {
    LocalizedText,
    getBase64,
    showSuccessAlert,
    CustomErrorType,
    CustomErrorParams,
    createZodFormResolver,
    useFormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useUploadProfilePhoto } from './queries/useUploadProfilePhoto';
import { PHOTO_FILE_SIZE_LIMIT } from './utils';

type Files = File[] | null;
export interface ProfilePictureFormData {
    files: Files;
}

export const ProfilePictureFormField: Record<string, keyof ProfilePictureFormData> = {
    Files: 'files',
};

const handleValidFiles = (files: Files) => files?.filter((file): file is File => file !== undefined) || [];

const UploadProfilePictureFormSchema = z.object({
    [ProfilePictureFormField.Files]: z
        .instanceof(File)
        .array()
        .refine(
            (files: Files) => {
                const validFiles = handleValidFiles(files);
                return validFiles.length === 1;
            },
            {
                params: { errorType: CustomErrorType.Other, textKey: TextKey.Required } satisfies CustomErrorParams,
            }
        )
        .refine(
            (files: Files) => {
                const validFiles = handleValidFiles(files);
                return validFiles.length === 0 || validFiles[0].size <= PHOTO_FILE_SIZE_LIMIT;
            },

            {
                params: { errorType: CustomErrorType.FileSize, maxSize: '5MB' } satisfies CustomErrorParams,
            }
        ),
});

const resolver = createZodFormResolver<ProfilePictureFormData>(UploadProfilePictureFormSchema);

export const useUploadProfilePhotoForm = ({ onClose }: { onClose: () => void }) => {
    const { mutate, isPending } = useUploadProfilePhoto();

    const handleSubmit = async ({ files }: ProfilePictureFormData) => {
        if (!files) {
            return;
        }

        await getBase64(files[0]).then((base64) => {
            mutate(base64, {
                onSuccess: () => {
                    showSuccessAlert({ title: <LocalizedText textKey={TextKey.SaveSuccessMessage} /> });
                    onClose();
                },
            });
        });
    };

    const { renderFormContent, submit } = useFormControls<ProfilePictureFormData>({
        onSubmit: handleSubmit,
        resolver,
        defaultValues: {
            files: null,
        },
    });

    return { renderFormContent, submit, isLoading: isPending };
};
